import type { AssetDocumentFolder } from '../hooks/useQueries'
import { computeOutlineNumbers } from '../components/hierarchy/outline-numbering'

/**
 * Grouping of an asset's documents into the folder hierarchy resolved for that
 * asset (spec 2026-07-27-06).
 *
 * Membership is derived **from the document type only** — a document lands in
 * the folder whose `document_type_uids` contains its `document_type_uid`. The
 * `documents.document_folder_id` FK is deliberately never read nor written:
 * the backend expands each folder's document type into its descendants, so the
 * mapping stays correct as the type tree evolves without any persistence.
 *
 * Anything left over (no type, or a type no folder claims) goes into the
 * trailing "unclassified" bucket.
 */

/** Minimal document shape needed for grouping. */
export interface GroupableDocument {
    uid: string
    document_type_uid?: string | null
}

export interface FolderTreeRow<D extends GroupableDocument> {
    folder: AssetDocumentFolder
    depth: number
    /**
     * Outline number of the folder (`1`, `1.1`, `1.1.1`, `2` …), **derived and
     * never persisted**: it is the folder's position among its siblings in the
     * layout.
     *
     * It is computed by `computeOutlineNumbers` — the very helper the
     * folder-layouts editor uses — over the *full* folder set, so a caller that
     * hides rows (`hideEmptyFolders`, a filtered documents list) never
     * renumbers the folders it still shows, and a folder is called `2.3`
     * everywhere it appears.
     */
    number: string
    documents: D[]
    /**
     * `documents.length` plus every descendant folder's own count, summed
     * recursively. A second, additive number alongside `documents` — it does
     * NOT change which folder a document belongs to (still exactly one, via
     * `documents`/membership), only what a collapsed parent row may choose to
     * display. See `DocumentFolderHeaderInfo.count` for which consumers use
     * this vs. the own-only `documents.length`.
     */
    rolledUpCount: number
}

export interface GroupedDocumentFolders<D extends GroupableDocument> {
    /** Folders in display order (pre-order DFS), each with its own documents. */
    rows: FolderTreeRow<D>[]
    /** Documents matching no folder — rendered as a trailing bucket. */
    unclassified: D[]
}

/**
 * Builds the ordered, depth-annotated folder rows and assigns each document to
 * exactly one folder. Folders are ordered by `ordering` within each parent and
 * nested through `parent_folder_uid`. Orphan folders (a `parent_folder_uid`
 * pointing at a folder absent from the payload) are treated as roots so nothing
 * is silently dropped.
 */
export function groupDocumentsByFolder<D extends GroupableDocument>(
    folders: AssetDocumentFolder[],
    documents: D[]
): GroupedDocumentFolders<D> {
    if (!folders.length) {
        return { rows: [], unclassified: [...documents] }
    }

    const byUid = new Map<string, AssetDocumentFolder>()
    for (const folder of folders) byUid.set(folder.uid, folder)

    const childrenByParent = new Map<string | null, AssetDocumentFolder[]>()
    for (const folder of folders) {
        const parent = folder.parent_folder_uid && byUid.has(folder.parent_folder_uid)
            ? folder.parent_folder_uid
            : null
        const siblings = childrenByParent.get(parent) ?? []
        siblings.push(folder)
        childrenByParent.set(parent, siblings)
    }
    for (const siblings of childrenByParent.values()) {
        // `uid` tie-break: the server orders ties by `id`, so a comparator that
        // stopped at `ordering` would be non-total here and the asset Documents
        // tab could render legacy duplicate-ordering folders in a different
        // order than the layout editor. Same rule as
        // `groupChildrenByParent` in components/hierarchy/outline-numbering.ts.
        siblings.sort(
            (a, b) => (a.ordering ?? 0) - (b.ordering ?? 0) || a.uid.localeCompare(b.uid)
        )
    }

    // document type uid -> folder uid
    const folderByTypeUid = new Map<string, string>()
    for (const folder of folders) {
        for (const typeUid of folder.document_type_uids ?? []) {
            if (!folderByTypeUid.has(typeUid)) folderByTypeUid.set(typeUid, folder.uid)
        }
    }

    const documentsByFolderUid = new Map<string, D[]>()
    const unclassified: D[] = []
    for (const doc of documents) {
        const folderUid = doc.document_type_uid ? folderByTypeUid.get(doc.document_type_uid) : undefined
        if (!folderUid) {
            unclassified.push(doc)
            continue
        }
        const bucket = documentsByFolderUid.get(folderUid) ?? []
        bucket.push(doc)
        documentsByFolderUid.set(folderUid, bucket)
    }

    // Outline numbers come from the hierarchy helper the folder-layouts editor
    // renders with (`computeOutlineNumbers(nodes, true)`, see
    // routes/$org/folder-layouts/$layoutUid.tsx) instead of being re-derived
    // from the traversal below: the number a user reads on an asset must be the
    // number the admin sees in the editor, and sharing the implementation is
    // the only way that stays true. `label` is unused for orderable trees (ties
    // break on `uid`, same rule as the sibling sort above) but the shape
    // requires it.
    const outlineNumbers = computeOutlineNumbers(
        folders.map(folder => ({
            uid: folder.uid,
            label: folder.code ?? '',
            parentUid: folder.parent_folder_uid ?? null,
            ordering: folder.ordering ?? 0,
        })),
        true
    )

    // Bottom-up: own count plus every descendant's own count. Memoized since
    // siblings share the same descendants-of-descendants work; cycle guard
    // mirrors the traversal below (a folder pointing back at an ancestor stops
    // rather than recursing forever).
    const rolledUpCounts = new Map<string, number>()
    const computeRolledUp = (folderUid: string, ancestors: Set<string>): number => {
        if (rolledUpCounts.has(folderUid)) return rolledUpCounts.get(folderUid)!
        if (ancestors.has(folderUid)) return documentsByFolderUid.get(folderUid)?.length ?? 0 // cycle guard
        const ownCount = documentsByFolderUid.get(folderUid)?.length ?? 0
        const childAncestors = new Set(ancestors).add(folderUid)
        const childrenSum = (childrenByParent.get(folderUid) ?? [])
            .reduce((sum, child) => sum + computeRolledUp(child.uid, childAncestors), 0)
        const total = ownCount + childrenSum
        rolledUpCounts.set(folderUid, total)
        return total
    }
    for (const folder of folders) computeRolledUp(folder.uid, new Set())

    const rows: FolderTreeRow<D>[] = []
    const visited = new Set<string>()
    const walk = (parentUid: string | null, depth: number) => {
        for (const folder of childrenByParent.get(parentUid) ?? []) {
            if (visited.has(folder.uid)) continue // cycle guard
            visited.add(folder.uid)
            rows.push({
                folder,
                depth,
                number: outlineNumbers.get(folder.uid) ?? '',
                documents: documentsByFolderUid.get(folder.uid) ?? [],
                rolledUpCount: rolledUpCounts.get(folder.uid) ?? 0,
            })
            walk(folder.uid, depth + 1)
        }
    }
    walk(null, 0)

    return { rows, unclassified }
}
